import { useEffect, useState } from 'react'

export type ThemeChoice = 'system' | 'light' | 'dark'

const STORAGE_KEY = 'greencandle.theme'
const ORDER: ThemeChoice[] = ['system', 'light', 'dark']
const LABEL: Record<ThemeChoice, string> = {
  system: 'System',
  light: 'Light',
  dark: 'Dark',
}

function stored(): ThemeChoice {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY)
    return value === 'light' || value === 'dark' ? value : 'system'
  } catch {
    return 'system'
  }
}

/**
 * Applies the choice by stamping `data-theme` on the root element, which is what the two
 * `[data-theme]` scopes in `styles.css` hang off. "System" removes the attribute entirely
 * so the media query takes over again - setting it to the current OS value instead would
 * freeze the page against a later OS change.
 */
function apply(choice: ThemeChoice): void {
  if (choice === 'system') {
    document.documentElement.removeAttribute('data-theme')
  } else {
    document.documentElement.setAttribute('data-theme', choice)
  }
}

export function ThemeToggle() {
  const [choice, setChoice] = useState<ThemeChoice>(stored)

  useEffect(() => {
    apply(choice)
    try {
      if (choice === 'system') {
        window.localStorage.removeItem(STORAGE_KEY)
      } else {
        window.localStorage.setItem(STORAGE_KEY, choice)
      }
    } catch {
      // A browser refusing storage still gets the theme for this tab.
    }
  }, [choice])

  const next = ORDER[(ORDER.indexOf(choice) + 1) % ORDER.length]!

  return (
    <button
      type="button"
      onClick={() => setChoice(next)}
      title={`Theme: ${LABEL[choice]}. Switch to ${LABEL[next]}.`}
    >
      {LABEL[choice]}
    </button>
  )
}
